import { useState, useEffect } from 'react';
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc,
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { mapBeerReviewDoc } from '../utils/mapBeerReviewDoc';

/**
 * Custom hook to fetch the latest 20 beer reviews with beer, brewery, and user information.
 * Returns reviews array with beer name, brewery name, and user name populated.
 */
function useBeerReviewsList() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchReviews = async () => {
      try {
        if (!cancelled) {
          setLoading(true);
          setError(null);
        }

        // Fetch latest 20 beer reviews ordered by date desc
        const reviewsRef = collection(db, 'beerReview');
        const reviewsQuery = query(
          reviewsRef,
          orderBy('date', 'desc'),
          limit(20),
        );
        const reviewsSnapshot = await getDocs(reviewsQuery);

        if (cancelled) return;

        if (!reviewsSnapshot.empty) {
          // Fetch beer, brewery, and user data for each review
          const reviewsData = await Promise.all(
            reviewsSnapshot.docs.map(async (reviewDoc) => {
              const reviewData = reviewDoc.data();
              const beerId = reviewData.beerId;
              const breweryId = reviewData.breweryId;
              const userId = reviewData.uid;

              // Fetch beer name
              let beerName = 'Unknown Beer';
              if (beerId) {
                try {
                  const beerDocRef = doc(db, 'beer', beerId);
                  const beerDoc = await getDoc(beerDocRef);
                  if (beerDoc.exists()) {
                    beerName = beerDoc.data().name || beerName;
                  }
                } catch (err) {
                  console.error(`Error fetching beer ${beerId}:`, err);
                }
              }

              // Fetch brewery name
              let breweryName = 'Unknown Brewery';
              if (breweryId) {
                try {
                  const breweryDocRef = doc(db, 'breweries', breweryId);
                  const breweryDoc = await getDoc(breweryDocRef);
                  if (breweryDoc.exists()) {
                    breweryName = breweryDoc.data().name || breweryName;
                  }
                } catch (err) {
                  console.error(`Error fetching brewery ${breweryId}:`, err);
                }
              }

              // Fetch user name
              let userName = 'Unknown User';
              if (userId) {
                try {
                  const userDocRef = doc(db, 'users', userId);
                  const userDoc = await getDoc(userDocRef);
                  if (userDoc.exists()) {
                    const userData = userDoc.data();
                    userName =
                      userData.userName || userData.displayName || userName;
                  }
                } catch (err) {
                  console.error(`Error fetching user ${userId}:`, err);
                }
              }

              return mapBeerReviewDoc({
                reviewId: reviewDoc.id,
                reviewData,
                beerName,
                breweryName,
                userName,
              });
            }),
          );

          if (!cancelled) {
            setReviews(reviewsData);
          }
        } else {
          if (!cancelled) {
            setReviews([]);
          }
        }
      } catch (err) {
        console.error('Error fetching beer reviews:', err);
        if (!cancelled) {
          setError(err.message);
          setReviews([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchReviews();

    return () => {
      cancelled = true;
    };
  }, []);

  return { reviews, loading, error };
}

export default useBeerReviewsList;

